import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { KINETIC_WORD_SETTING } from './kineticWordSetting';
import { check, hasLongTravel, repeatsAClass, treatsSeveral } from '../skillUsage';

export const wordTravelsDirectionSkill: CodeoSkill = {
    name: SKILL_NAMES.wordTravelsDirection,
    kind: 'codeo',
    description:
        'Move a word the way it says — up for rise, down for fall, forward for advance — its ' +
        'letters strung out along the path as they travel and bunching up as they arrive, so the ' +
        'direction is the meaning. For words of direction and movement.',
    checkLooksFor: 'per-letter spans travelling a long way (x or y), one after another',
    documentUsesSkill: check(
        (script, markup) =>
            repeatsAClass(markup, 'span') && treatsSeveral(script) && hasLongTravel(script),
    ),
    instructions: `# Word Travels Its Direction

The word goes where it says. "Up" climbs the frame, "down" drops through it, "forward" drives across it left to right, "back" retreats right to left. The letters do not travel as a block: **each one sets off a moment after the one before**, so in flight the word is strung out along the path like a staircase, and as the leaders slow to a stop **the followers catch up** and the word closes back into a line. The stagger is what makes it read as motion along a direction rather than a slide.

Use it on a word of direction or movement: rise, climb, up, grow, lift; fall, drop, sink, down; forward, advance, ahead, faster; back, behind, retreat.

${KINETIC_WORD_SETTING}

## Markup

Capitals in \`inline-block\` letter spans.

\`\`\`html
<div class="absolute inset-0 flex items-center justify-center">
  <div id="travel-word" class="tw-word" data-l5-allow-literal>
    <span class="tw-ch">R</span><span class="tw-ch">I</span><span class="tw-ch">S</span><span class="tw-ch">E</span>
  </div>
</div>
\`\`\`

\`\`\`css
.tw-word { line-height: 1; white-space: nowrap; font-family: var(--l5-font-0), sans-serif; font-weight: 800; text-transform: uppercase; color: var(--l5-color-3, #fafafa); }
.tw-ch { display: inline-block; }
\`\`\`

## Timeline code — up

From below the frame to its resting place, then on out through the top.

\`\`\`js
const tl = gsap.timeline({ paused: true });
fitWord(document.querySelector('#travel-word'), 0.7);
const letters = [...document.querySelectorAll('#travel-word .tw-ch')];

const CUE = 0.5, CUE_NEXT = 1.6;
const TRAVEL = 1080 * 0.85;      // most of the frame: the journey is the point
const LAG = 0.05;                // three frames between letters: the staircase
letters.forEach((ch, i) => {
  tl.fromTo(ch, { y: TRAVEL }, { y: 0, duration: 0.45, ease: 'power3.out' }, CUE - 0.3 + i * LAG);
  tl.to(ch, { y: -TRAVEL, duration: 0.3, ease: 'power3.in' }, CUE_NEXT - 0.3 + i * LAG * 0.6);
});
tl.set({}, {}, 2.0);
window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Down**: flip the signs: in from above, out through the bottom. For a hard landing, use \`${SKILL_NAMES.glyphDropAndLand}\` instead.
- **Forward / back**: travel on \`x\` by about the frame's width, the leading letter first (rightmost letter leads going forward, leftmost going back).
- **Climbing in steps**: for "climb" or "step up", break the travel into three or four hops, each a short \`power2.out\` rise with a held pause between, every letter on the same steps.
- **Accelerating**: for "faster" or "speed up", shorten the lag as the word travels and use \`expo.in\` on the exit, plus \`${SKILL_NAMES.motionBlurStreak}\` on the fastest part.

## Pitfalls

- **Far, not a nudge.** The travel is most of the frame. A word that moves 60px up has not risen; it has twitched.
- **The stagger carries the direction.** Without it the word slides as a block and reads as a pan.
- **Readable at rest.** The letters close up fully before the word holds; never leave the staircase as the resting pose.
- The letters are transform-tweened, so they carry no CSS \`transform\`.`,
};
